import React from 'react';
import { PlayCircle, X, Clock } from 'lucide-react';
// ✅ Import centralized theme
import { gradients, buttonStyles, cardStyles, borderRadius, shadows } from '../shared/config/theme';
import { getJourneyConfig } from '../shared/constants/journeyConfig';

const ResumeSessionModal = ({ session, journey, onResume, onDismiss }) => {
  const getStepDisplay = (step) => {
    switch(step) {
      case 'chat': return { label: 'AI Coaching', icon: '💬', progress: '33%' };
      case 'journaling': return { label: 'Journaling', icon: '📝', progress: '66%' };
      case 'meditation': return { label: 'Meditation', icon: '🧘‍♀️', progress: '90%' };
      default: return { label: 'Session', icon: '✨', progress: '0%' };
    }
  };

  const stepInfo = getStepDisplay(session.current_step);
  
  // ✅ Get journey-specific styling
  const journeyStyle = getJourneyConfig(journey?.title || journey?.category);
  
  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const started = new Date(timestamp);
    const diffMs = now - started;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className={`${cardStyles.default.background} ${cardStyles.default.rounded} ${cardStyles.default.shadow} max-w-md w-full overflow-hidden animate-scale-in`}>
        {/* Header - Using journey-specific gradient */}
        <div className={`bg-gradient-to-br ${journeyStyle.gradient} p-6 relative`}>
          <button
            onClick={onDismiss}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <PlayCircle className="w-8 h-8 text-white" />
            <h2 className="text-white text-2xl font-bold">Continue Your Journey?</h2>
          </div>
          <p className="text-white/90 text-sm">
            You have an unfinished session waiting for you
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Journey Info - Using journey-specific card gradient */}
          <div className={`bg-gradient-to-br ${journeyStyle.cardGradient} ${cardStyles.default.rounded} p-4 mb-4`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-12 h-12 bg-gradient-to-br ${journeyStyle.gradient} ${cardStyles.default.rounded} flex items-center justify-center text-2xl ${shadows.lg}`}>
                {journey.emoji || journeyStyle.emoji}
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{journey.title}</h3>
                <p className="text-sm text-gray-600">{journey.category} journey</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-2">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Progress</span>
                <span>{stepInfo.progress} complete</span>
              </div>
              <div className="bg-gray-200 h-2 rounded-full overflow-hidden">
                <div 
                  className={`bg-gradient-to-r ${journeyStyle.gradient} h-full transition-all`}
                  style={{ width: stepInfo.progress }}
                />
              </div>
            </div>

            {/* Current Step */}
            <div className="flex items-center gap-2 text-sm text-gray-700 mt-3">
              <span className="text-lg">{stepInfo.icon}</span>
              <span>Currently at: <strong>{stepInfo.label}</strong></span>
            </div>
          </div>

          {/* Time Info */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Clock className="w-4 h-4" />
            <span>Started {getTimeAgo(session.started_at)}</span>
          </div>

          {/* Action Buttons - Using centralized button styles */}
          <div className="space-y-3">
            <button
              onClick={onResume}
              className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} py-4`}
            >
              Continue Session
            </button>
            
            <button
              onClick={onDismiss}
              className={`w-full ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} ${buttonStyles.secondary.shadow} py-3`}
            >
              Start Fresh Instead
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { 
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ResumeSessionModal;